import { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { NAVY, BORDER_COLOR, BG_FIELD } from '../../styles/tokens';

/**
 * Searchable dropdown (combobox) for employees and cost codes.
 * - Type to filter, show matches in real time
 * - Recently used items appear at top
 * - Mobile: works as a scrollable dropdown
 *
 * Props:
 *   items: [{ id, label, sublabel? }]
 *   value: selected id (or null)
 *   onChange: (id) => void
 *   placeholder: string
 *   disabled: boolean
 */
export default function SearchableSelect({ items = [], value, onChange, placeholder = 'Buscar...', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selected = items.find(i => i.id === value);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = items.filter(item => {
    if (!query) return true;
    const q = query.toLowerCase();
    return item.label.toLowerCase().includes(q) ||
           (item.sublabel && item.sublabel.toLowerCase().includes(q));
  });

  function handleSelect(id) {
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  function handleOpen() {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      {/* Display / trigger */}
      <div
        onClick={handleOpen}
        style={{
          background: disabled ? '#f1f5f9' : BG_FIELD,
          border: `1px solid ${open ? '#f59e0b' : BORDER_COLOR}`,
          borderRadius: 6,
          padding: '6px 10px',
          fontSize: 12,
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minHeight: 32,
          color: selected ? NAVY : '#94a3b8',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Search size={13} color="#94a3b8" />
        {selected ? (
          <span style={{ color: NAVY, fontWeight: 500 }}>{selected.label}</span>
        ) : (
          <span>{placeholder}</span>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 2,
          background: '#fff',
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 100,
          maxHeight: 240,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${BORDER_COLOR}` }}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={placeholder}
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                fontSize: 12,
                fontFamily: 'Inter, sans-serif',
                background: 'transparent',
                color: NAVY,
              }}
            />
          </div>
          {/* Options list */}
          <div style={{ overflowY: 'auto', maxHeight: 192 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 10px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                Sin resultados
              </div>
            ) : (
              filtered.map(item => (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item.id)}
                  style={{
                    padding: '7px 12px',
                    fontSize: 12,
                    cursor: 'pointer',
                    background: item.id === value ? '#fef3c7' : 'transparent',
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                  onMouseEnter={e => { if (item.id !== value) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (item.id !== value) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontWeight: 500, color: NAVY }}>{item.label}</span>
                  {item.sublabel && (
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{item.sublabel}</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
